import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import { useGetCountries } from "api/queries/common/useGetCountries";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import TimeInput from "components/atoms/TimeInput/TimeInput";
import moment from "moment";
import Textarea from "components/atoms/Textarea/Textarea";
import { StopTimeType } from "types/MultiStop";
import DropPlanningSidebar from "./DropPlanningSidebar";
import useScheduleHourNotesHook from "../../hooks/useScheduleHourNotesHook";
import PickupDeliveryDate from "../../PickupDeliveryDate";
import { multiDropDeliveryTimeTypeOptions, multiDropPickupTimeTypeOptions } from "../../ScheduleDefaultOptions";

interface DropPlanningFormProps {
  index: number;
}

export default function DropPlanningForm({ index }: DropPlanningFormProps) {
  const intl = useIntl();
  const [holidays, setHolidays] = useState<any>({});
  const { watch, getValues } = useFormContext();

  const { data: countries = [] } = useGetCountries({
    select: (data) => data.filter((country: { label: string }) => country.label !== "USA"),
    refetchOnMount: false,
  });

  const { showHour, showNotes, handleShowHour, handleShowNotes } = useScheduleHourNotesHook({
    hoursFromKey: `schedule.stops.${index}.stop_hours_start`,
    hoursToKey: `schedule.stops.${index}.stop_hours_end`,
    notesKey: `schedule.stops.${index}.stop_notes`,
  });

  const isPickup = index === 0;
  const isBetweenStopTimeType = watch(`schedule.stops.${index}.stop_time_type`) === StopTimeType.BETWEEN;
  const isValidHours = (value: any): boolean =>
    moment(value, "HH:mm:ss").isAfter(moment(watch(`schedule.stops.${index}.stop_hours_start`), "HH:mm:ss"));

  const getValuesForValidation = () => {
    const output = {
      previousAfterDate: getValues(`schedule.stops.${index - 1}.stop_after_date`),
      previousAfterTime: getValues(`schedule.stops.${index - 1}.stop_after_time`),
      currentAfterDate: getValues(`schedule.stops.${index}.stop_after_date`),
      currentBeforeDate: getValues(`schedule.stops.${index}.stop_before_date`),
      currentAfterTime: getValues(`schedule.stops.${index}.stop_after_time`),
      currentBeforeTime: getValues(`schedule.stops.${index}.stop_before_time`),
    };
    return {
      ...output,
      previousAfterDateTime: moment(`${output?.previousAfterDate} ${output?.previousAfterTime}`, "YYYY-MM-DD HH:mm:ss"),
      currentAfterDateTime: moment(`${output?.currentAfterDate} ${output?.currentAfterTime}`, "YYYY-MM-DD HH:mm:ss"),
      currentBeforeDateTime: moment(`${output?.currentBeforeDate} ${output?.currentBeforeTime}`, "YYYY-MM-DD HH:mm:ss"),
    };
  };

  const checkValidAfterDate = () => {
    if (isPickup) return true;
    const {
      previousAfterDate,
      previousAfterTime,
      previousAfterDateTime,
      currentAfterDate,
      currentAfterTime,
      currentAfterDateTime,
    } = getValuesForValidation();
    const haveValidHours =
      moment(previousAfterTime, "HH:mm:ss", true).isValid() && moment(currentAfterTime, "HH:mm:ss", true).isValid();

    if (haveValidHours) {
      return moment(previousAfterDateTime).isBefore(moment(currentAfterDateTime));
    }

    return moment(previousAfterDate).isSameOrBefore(moment(currentAfterDate));
  };

  const checkValidBeforeDate = () => {
    const {
      currentAfterTime,
      currentAfterDateTime,
      currentAfterDate,
      currentBeforeTime,
      currentBeforeDateTime,
      currentBeforeDate,
    } = getValuesForValidation();

    const haveValidHours =
      moment(currentBeforeTime, "HH:mm:ss", true).isValid() && moment(currentAfterTime, "HH:mm:ss", true).isValid();

    if (haveValidHours) {
      return moment(currentAfterDateTime).isBefore(moment(currentBeforeDateTime));
    }

    return moment(currentAfterDate).isSameOrBefore(moment(currentBeforeDate));
  };

  const stopPositionLabel =
    index === 1
      ? intl.formatMessage({ id: "general.pickup", defaultMessage: "Pickup" }).toLowerCase()
      : intl
          .formatMessage(
            { id: "new-request.schedule.deliveryStop", defaultMessage: "Delivery stop #{stopNumber}" },
            { stopNumber: index - 1 },
          )
          .toLowerCase();

  return (
    <div className="d-flex mt-3xl mb-3xl">
      <DropPlanningSidebar
        index={index}
        showHour={showHour}
        showNotes={showNotes}
        handleShowHour={handleShowHour}
        handleShowNotes={handleShowNotes}
      />

      <div>
        <div className="w-192 dropdown-div mb-lg">
          <Dropdown
            useHookForm
            name={`schedule.stops.${index}.stop_time_type`}
            options={isPickup ? multiDropPickupTimeTypeOptions(intl) : multiDropDeliveryTimeTypeOptions(intl)}
            required
          />
        </div>

        {/* First row */}
        <div className="flex-row flex-wrap main-wrapper">
          <div className="w-192 align-date">
            <PickupDeliveryDate
              datePickerProps={{
                name: `schedule.stops.${index}.stop_after_date`,
                required: true,
                validations: {
                  isValidDeliveryDate: () =>
                    checkValidAfterDate() ||
                    intl.formatMessage(
                      {
                        id: "new-request.schedule.multiDropAfterDateError",
                        defaultMessage: "Stop date must be after {stopPosition} date.",
                      },
                      {
                        stopPosition: stopPositionLabel,
                      },
                    ),
                },
              }}
              countries={countries}
              holidays={holidays}
              setHolidays={setHolidays}
            />
          </div>
          <TimeInput
            name={`schedule.stops.${index}.stop_after_time`}
            required
            defaultValue={isPickup ? "09:00:00" : "17:00:00"}
            registerOptions={{
              deps: [`schedule.stops.${index}.stop_after_date`, `schedule.stops.${index}.stop_after_time`],
            }}
          />
          <div className="w-192 timezone dropdown-div">
            <Dropdown
              useHookForm
              name={`schedule.stops.${index}.timezone`}
              options={watch(`schedule.stops.${index}.timezone`) ? [watch(`schedule.stops.${index}.timezone`)] : []}
              placeholder={intl.formatMessage({
                id: "new-request.schedule.addressBookDialog.timezone",
                defaultMessage: "Timezone",
              })}
              disabled
            />
          </div>
        </div>
        {/* END */}

        {/* Between Date, Time, and Timezone */}
        {isBetweenStopTimeType && (
          <div className="flex-row mt-2 main-wrapper">
            <div className="w-192 align-date">
              <PickupDeliveryDate
                datePickerProps={{
                  name: `schedule.stops.${index}.stop_before_date`,
                  required: true,
                  validations: {
                    isValidDeliveryDate: () =>
                      checkValidBeforeDate() ||
                      intl.formatMessage(
                        {
                          id: "new-request.schedule.multiDropBeforeDateError",
                          defaultMessage: "End date must be after start date",
                        },
                        {
                          stopPosition: stopPositionLabel,
                        },
                      ),
                  },
                }}
                countries={countries}
                holidays={holidays}
                setHolidays={setHolidays}
              />
            </div>
            <TimeInput
              name={`schedule.stops.${index}.stop_before_time`}
              required
              registerOptions={{
                deps: [`schedule.stops.${index}.stop_before_date`, `schedule.stops.${index}.stop_before_time`],
              }}
            />
          </div>
        )}
        {/* END */}

        {/* Hours Start and Hours End */}
        {showHour && (
          <div className="flex-row">
            <div className="w-87 mt-2">
              <label>
                <FormattedMessage id="new-request.schedule.specifyHoursFrom" defaultMessage="From" />
              </label>
              <TimeInput
                name={`schedule.stops.${index}.stop_hours_start`}
                defaultValue="09:00:00"
                required
                registerOptions={{
                  deps: `schedule.stops.${index}.stop_hours_start`,
                }}
              />
            </div>
            <div className="w-87 mt-2">
              <label>
                <FormattedMessage id="new-request.schedule.specifyHoursTo" defaultMessage="To" />
              </label>
              <TimeInput
                name={`schedule.stops.${index}.stop_hours_end`}
                defaultValue="18:00:00"
                required
                validations={{
                  isValidPickupHours: (v: any) =>
                    isValidHours(v) ||
                    intl.formatMessage({
                      id: "new-request.schedule.specifyPickupHourErrorMessage",
                      defaultMessage: "End time should be greater than start time",
                    }),
                }}
              />
            </div>
          </div>
        )}
        {/* END */}

        {/* Notes */}
        {showNotes && (
          <Textarea
            name={`schedule.stops.${index}.stop_notes`}
            placeholder={intl.formatMessage({
              id: `${isPickup ? "new-request.schedule.pickupInstructions" : "new-request.schedule.deliveryInstructions"}`,
              defaultMessage: `${isPickup ? "Add pickup instructions" : "Add delivery instructions"}`,
            })}
            rows={2}
            className="text-body bg-white w-392 mt-3"
          />
        )}
        {/* END */}
      </div>
    </div>
  );
}
